<template>
	<div class="rentabilidad_tab report_theme_container" data-theme="rentabilidad">
		<!-- Mismo set de sub_nav_filters (Mensual/Trimestral/YTD/Anual) y mismo
			selector de RANGO que ResultadosTab. Comparten el `filterPeriod` del
			store financialReports y los filtros de fecha (endMonth, customRange,
			customYearRange) de reportsFilters → cambiar el bucket o el rango acá
			lo refleja también en Resultados al volver, y viceversa. -->
		<TabHeader
			title="Rentabilidad"
			description="Analiza la tendencia y composición de la rentabilidad de tu negocio"
			:filterPeriod="filterPeriod"
			:filterOptions="visiblePeriodOptions"
			:endMonth="endMonth"
			:getNow="getNow"
			calendar-mode="range"
			:date-range="calendarRange"
			:year-range="yearRange"
			table-title="Evolución de Ventas, Márgenes & Costos"
			table-title-collapsible
			:table-expanded="chartsExpanded"
			@toggle-table="toggleCharts"
			@update-filter-period="updateFilterPeriod"
			@update-end-month="updateEndMonth"
			@update-date-range="updateDateRange"
			@update-year-range="updateYearRange">
			<!-- Chips de categoría (Tiendas, Marcas, …): acá NO reagrupan la
				tabla como en Ventas — abren el drawer "Análisis de Categorías"
				con la rentabilidad del mes por ítem de esa categoría. -->
			<template v-if="categoryChips.length" #center-sub-nav>
				<div class="sub_nav_filters">
					<button v-for="chip in categoryChips" :key="chip.id"
						type="button"
						class="filter_btn"
						@click="openCategoryAnalysis(chip.id)">
						{{ chip.label }}
					</button>
				</div>
			</template>
		</TabHeader>

		<!-- ================================================================ -->
		<!-- Evolución de Ventas, Márgenes & Costos                            -->
		<!-- Dos gráficos en una fila, sobre las MISMAS filas y cabeceras que  -->
		<!-- la tabla de abajo (sin fetch propio): a la izquierda barras de     -->
		<!-- Ventas + una línea por escalón de margen; a la derecha una línea   -->
		<!-- por cuenta de costo del plan más Gastos Totales, en pesos o en %   -->
		<!-- de ventas. Mismo patrón que Ventas/Costos/Gastos: el título        -->
		<!-- colapsable vive en el header sticky y el cuerpo (headless) scrollea -->
		<!-- con el contenido; la preferencia se comparte con esos tabs.        -->
		<!-- ================================================================ -->
		<CollapsibleSection class="mt-1"
			headless
			:controlled-expanded="chartsExpanded">
			<!-- Con la comparación abierta la card de márgenes ocupa la fila
				completa (el chart por año necesita el ancho) y la de costos
				se oculta hasta volver a "Ver Barra". -->
			<div class="charts_row" :class="{ 'is-comparison': comparisonOpen }">
				<ProfitabilityChartCard
					:headers="getHeadersTable"
					:rows="tableRowHeaders"
					:is-loading="isLoading"
					:error="error || ''"
					:comparison-open="comparisonOpen"
					:comparison="comparisonViewModel"
					@toggle-comparison="onToggleComparison"
					@comparison-select-metric="onComparisonSelectMetric"
					@comparison-add-year="onComparisonAddYear"
					@comparison-remove-year="onComparisonRemoveYear" />
				<ProfitabilityCostsChartCard v-if="!comparisonOpen"
					:headers="getHeadersTable"
					:rows="tableRowHeaders"
					:is-loading="isLoading"
					:error="error || ''"
					:percent-mode.sync="costsPercentMode" />
			</div>
		</CollapsibleSection>

		<!-- ================================================================ -->
		<!-- Evolución de Rentabilidad                                         -->
		<!-- Reusa FinancialResultsTable (misma usada por ResultadosTab) con    -->
		<!-- las filas filtradas y reordenadas para mostrar Ventas + 5          -->
		<!-- escalones de margen, cada uno con su pareja money/%.               -->
		<!-- ================================================================ -->
		<!-- El rango lo gobierna el selector del header (como en Resultados):
			el select "Último año / 24 meses / …" de la barra de la tabla no
			puede expresar un rango custom y se oculta. -->
		<FinancialResultsTable
			class="mt-2"
			:rows="profitabilityRows"
			:headers="getHeadersTable"
			:isLoading="isLoading"
			:filterPeriod="filterPeriod"
			:show-period-select="false">
			<template #title>
				<ChartLine class="title_icon mr-2" />
				<span class="tittle_section">Evolución de Rentabilidad</span>
			</template>
			<!-- Sin acciones propias: la comparación y la vista de barras viven
				en la card del gráfico de arriba (Steve, 2026-10-01). -->
		</FinancialResultsTable>
	</div>
</template>

<script>
import moment from "moment";
import { mapState, mapGetters, mapActions, mapMutations } from "vuex";
import ChartLine from "@/components/svg/ChartLine.vue";
import TabHeader from "@/components/financialReports/TabHeader.vue";
import CollapsibleSection from "@/components/financialReports/CollapsibleSection.vue";
import ProfitabilityChartCard from "@/components/financialReports/ProfitabilityChartCard.vue";
import ProfitabilityCostsChartCard from "@/components/financialReports/ProfitabilityCostsChartCard.vue";
import FinancialResultsTable from "@/components/financialReports/FinancialResultsTable.vue";
import { FILTER_PERIOD_OPTIONS } from "@/config/reportStructure";
import { displayMonthRange, displayYearRange } from "@/store/financialReports/utils/dateRange";
import { profitabilityAnalysisModalConfig }
	from "@/components/financialReports/profitabilityAnalysis/profitabilityDrawerModals";
import { readBoolFlag, writeBoolFlag } from "@/utils/localFlag";
import { PROFITABILITY_MARGINS } from "@/utils/profitabilityMargins";
import { capitalize } from "@/utils/string";

// Misma preferencia que Ventas/Costos/Gastos: plegar los gráficos en un tab
// los pliega en todos (la sección ES los gráficos del tab).
const CHARTS_EXPANDED_KEY = "financialReports:charts-expanded";

// Métricas del modo comparación de la card (chips de selección única): clave
// del backend (`metric` de /profitability/comparison) ↔ label del chip.
// `rowKey`: la fila del store que tiene que existir para ofrecer el chip —
// los escalones salen de la estructura del plan de cuentas (Margen Producto
// solo si hay Costos de Productos, Contribución solo si hay Costos Fijos).
// Los escalones vienen de la spec compartida (`utils/profitabilityMargins`).
const COMPARISON_METRICS = [
	{ key: "ventas", label: "Venta", unit: "money", rowKey: null },
	...PROFITABILITY_MARGINS.map((margin) => ({
		key: margin.metricKey,
		label: margin.shortLabel,
		unit: "percent",
		rowKey: margin.rowKey,
	})),
];

// Las filas de Rentabilidad CONSUMEN las filas ya calculadas del store
// (tableRowHeaders): las fórmulas de los escalones vienen en la estructura
// que arma el backend desde el plan de cuentas, y acá solo se elige QUÉ
// filas mostrar, con el theme propio del tab. Se muestran las que la
// estructura trae: un plan sin Costos de Productos no tiene Margen Producto,
// uno sin Costos Fijos no tiene Contribución (antes era por super_type).
const PROFITABILITY_ROWS = [
	{ key: "ventas_netas" },
	{ key: "margen_producto_amount" },
	{ key: "margen_producto", theme: "theme_row_purple" },
	{ key: "margen_bruto_amount" },
	{ key: "margen_bruto", theme: "theme_row_purple" },
	{ key: "margen_contribucion_amount" },
	{ key: "margen_contribucion", theme: "theme_row_azure" },
	{ key: "EBITDA" },
	{ key: "margen_EBITDA_%", theme: "theme_row_purple" },
	{ key: "utilidad_antes_de_impuestos" },
	{ key: "margen_neto_%", theme: "theme_row_yellow" },
];

export default {
	name: "RentabilidadTab",

	components: {
		TabHeader,
		CollapsibleSection,
		ChartLine,
		ProfitabilityChartCard,
		ProfitabilityCostsChartCard,
		FinancialResultsTable,
	},

	data() {
		return {
			filterOptions: FILTER_PERIOD_OPTIONS,
			chartsExpanded: readBoolFlag(CHARTS_EXPANDED_KEY),
			// Estado de vista de la card: gráfico compuesto ↔ comparación por
			// año. Local (no se comparte ni persiste), como en VentasTab.
			comparisonOpen: false,
			// "% de ventas" del gráfico de costos: pesos ↔ share sobre las
			// ventas de cada cabecera. Local, no persiste.
			costsPercentMode: false,
		};
	},

	computed: {
		...mapState("reportsFilters", [
			"dataFirstMonth",
			"endMonth",
			"selectedPeriodRange",
			"customRange",
			"customYearRange",
		]),
		...mapState("financialReports", [
			"filterPeriod",
			"tableRowHeaders",
			"isLoading",
			"error",
		]),
		...mapState("classification", ["categories", "itemsByCategory"]),
		...mapState("profitabilityAnalysis", {
			analysisMonth: "month",
			analysisCategory: "category",
			analysisSelectedItems: "selectedItems",
		}),
		...mapState("profitabilityReport", [
			"comparisonData",
			"isComparisonLoading",
			"comparisonError",
			"comparisonYears",
			"comparisonMetric",
		]),
		...mapGetters("financialReports", {
			getHeadersTableFunc: "getHeadersTable",
		}),

		getNow() {
			return moment().toDate();
		},

		// Rango activo del calendar del header (modo range, igual que Resultados).
		// Con `customRange` en el store se usa tal cual; si no, el default
		// (últimos 12 meses sobre endMonth) para que el picker abra pintando
		// "Último año" en vez de "—". Es sólo visual: el fetch sigue usando
		// selectedPeriodRange hasta que el usuario elija un rango.
		calendarRange() {
			return displayMonthRange(this.endMonth, this.customRange, this.dataFirstMonth);
		},
		// Rango de años del picker de Anual: el custom elegido, o el default de
		// Anual (mismo que usa el fetch) para que el trigger pinte el span real.
		yearRange() {
			return displayYearRange(this.endMonth, this.customYearRange, this.dataFirstMonth);
		},

		// Períodos visibles en el header. Con la comparación abierta YTD/Anual
		// no aplican (curvas mensuales/trimestrales año-contra-año), igual que
		// en Ventas: sólo Mensual y Trimestral mientras dure.
		visiblePeriodOptions() {
			if (!this.comparisonOpen) return this.filterOptions;
			return this.filterOptions.filter((o) => o.id === "month" || o.id === "quarter");
		},

		// Chips del header: las categorías configuradas de la empresa. No se
		// deshabilitan por falta de datos (Rentabilidad no pide un breakdown
		// que lo sepa de antemano): una categoría sin ítems en el mes abre el
		// drawer con su estado vacío.
		categoryChips() {
			return (this.categories || []).map((category) => ({
				id: category,
				label: capitalize(category),
			}));
		},

		// Selects del header del drawer: uno por categoría, con sus ítems.
		analysisCategories() {
			return (this.categories || []).map((category) => ({
				id: category,
				label: capitalize(category),
				options: (this.itemsByCategory[category] || []).map((name) => ({
					value: name,
					label: name,
				})),
			}));
		},

		comparisonMetrics() {
			return COMPARISON_METRICS.filter((m) => !m.rowKey || this.hasRow(m.rowKey));
		},

		activeComparisonMetric() {
			return COMPARISON_METRICS.find((m) => m.key === this.comparisonMetric)
				|| COMPARISON_METRICS[0];
		},

		// View-model para SalesComparisonChart (sus keys calzan 1:1 con las
		// props). Los chips de métrica viajan como `subItems` (labels) y el
		// chart devuelve el label elegido en `select-item`.
		comparisonViewModel() {
			const metric = this.activeComparisonMetric;
			return {
				series: this.comparisonData ? this.comparisonData.series : [],
				months: this.comparisonData ? this.comparisonData.months : [],
				availableYears: this.comparisonData ? this.comparisonData.availableYears : [],
				isLoading: this.isComparisonLoading,
				error: this.comparisonError || "",
				subItems: this.comparisonMetrics.map((m) => m.label),
				selectedItem: metric.label,
				years: this.comparisonYears,
				valueFormat: metric.unit,
				// Trimestral agrega SUMANDO los meses de cada quarter: vale para
				// las ventas en pesos, no para un margen en % (sumar tres
				// porcentajes no es el margen del trimestre). Los márgenes se
				// comparan siempre mes a mes.
				period: metric.unit === "money" && this.filterPeriod === "quarter"
					? "quarter"
					: "month",
			};
		},

		getHeadersTable() {
			return this.getHeadersTableFunc();
		},

		profitabilityRows() {
			return PROFITABILITY_ROWS
				.filter(spec => this.hasRow(spec.key))
				.map(spec => {
					const row = this.tableRowHeaders[spec.key] || {};
					return {
						id: spec.key,
						label: row.label,
						isMoney: row.isMoney,
						theme: spec.theme,
						itemsDict: row.itemsDict || {},
					};
				});
		},

		// Firma reactiva consolidada — un solo trigger para fetch aunque varios
		// filtros cambien en la misma transacción. Incluye los rangos custom
		// para refetchear cuando el usuario mueve el rango (antes faltaban: un
		// rango elegido en Resultados cambiaba las cabeceras pero no los datos).
		fetchSignature() {
			return [
				this.filterPeriod,
				this.endMonth,
				this.selectedPeriodRange,
				this.customRange && this.customRange.from,
				this.customRange && this.customRange.to,
				this.customYearRange && this.customYearRange.from,
				this.customYearRange && this.customYearRange.to,
			].join("|");
		},
	},

	mounted() {
		this.fetchReportData();
		// Alimenta `categories` + `itemsByCategory` (chips del header y selects
		// del drawer). La action hace short-circuit si ya se pidió.
		this.getCategories();
	},

	methods: {
		...mapActions("classification", ["getCategories"]),
		...mapActions("financialReports", ["fetchReportData"]),
		...mapActions("profitabilityAnalysis", ["fetchProfitabilityAnalysis"]),
		...mapActions("profitabilityReport", ["fetchProfitabilityComparison"]),
		...mapActions("reportsFilters", ["setCustomRange", "setCustomYearRange"]),
		...mapMutations("reportsFilters", ["SET_END_MONTH"]),
		...mapMutations("financialReports", ["SET_FILTER_PERIOD"]),
		...mapMutations("profitabilityReport", ["SET_COMPARISON_YEARS", "SET_COMPARISON_METRIC"]),
		...mapMutations("profitabilityAnalysis", { RESET_ANALYSIS: "RESET" }),

		// Una fila existe si la estructura del plan la trae (antes del primer
		// fetch no hay ninguna: caso degradado conservador, sin filas).
		hasRow(key) {
			return Boolean(this.tableRowHeaders && this.tableRowHeaders[key]);
		},
		updateFilterPeriod(period) {
			this.SET_FILTER_PERIOD(period);
		},
		updateEndMonth(date) {
			this.SET_END_MONTH(date);
		},
		// El calendar del header (modo range) emite el rango elegido; la action
		// sincroniza customRange + selectedPeriodRange="custom" + endMonth.
		updateDateRange(range) {
			this.setCustomRange(range);
		},
		// El picker de Anual emite el rango de años; estado ortogonal al mes.
		updateYearRange(range) {
			this.setCustomYearRange(range);
		},
		// El título en el header sticky pliega/despliega el gráfico; la
		// preferencia sobrevive al refresh y al cambio de tab.
		toggleCharts() {
			this.chartsExpanded = !this.chartsExpanded;
			writeBoolFlag(CHARTS_EXPANDED_KEY, this.chartsExpanded);
		},

		// --- Drawer "Análisis de Categorías" ---
		// El chip abre el drawer con esa categoría como principal (sin nada
		// tildado = todos sus ítems) y el mes del header como mes focal.
		openCategoryAnalysis(category) {
			const month = moment(this.endMonth).format("YYYY-MM");
			this.fetchProfitabilityAnalysis({
				month, category, selectedItems: this.emptySelectionOf(category),
			});
			this.$buefy.modal.open({
				...profitabilityAnalysisModalConfig({
					periodLabel: moment(this.endMonth)
						.locale("es")
						.format("MMM YYYY")
						.replace(/^./, (c) => c.toUpperCase()),
					endMonth: moment(this.endMonth).toDate(),
					getNow: this.getNow,
					categories: this.analysisCategories,
				}),
				parent: this,
				events: {
					closed: this.onAnalysisClose,
					"update-end-month": this.onAnalysisMonthChange,
					"category-change": this.onAnalysisCategoryChange,
					"period-change": this.onAnalysisPeriodChange,
				},
			});
		},
		onAnalysisClose() {
			this.RESET_ANALYSIS();
		},
		// El segmented del drawer cambia la granularidad (mes, trimestre o
		// año que contiene el mes elegido); mismo mes, categoría y selección.
		onAnalysisPeriodChange(period) {
			this.fetchProfitabilityAnalysis({
				month: this.analysisMonth,
				category: this.analysisCategory,
				selectedItems: this.analysisSelectedItems,
				period,
			});
		},
		// El calendario del drawer cambia el mes analizado sin tocar el del tab.
		onAnalysisMonthChange(date) {
			this.fetchProfitabilityAnalysis({
				month: moment(date).format("YYYY-MM"),
				category: this.analysisCategory,
				selectedItems: this.analysisSelectedItems,
			});
		},
		// Selección inicial de una categoría: NADA tildado, que es "todos sus
		// ítems" (sin selección el store no manda filtro, ver
		// `effectiveFilters`). El header marca la principal con su estilo.
		emptySelectionOf(category) {
			return { [category]: [] };
		},
		// Un filtro del header acota ítems y, si es de otra categoría, la
		// vuelve la principal sin nada tildado (D7: pasar de Tiendas a Marcas
		// sin cerrar el drawer). Ya siendo la principal, se respeta lo que el
		// analista tilde o destilde ("Ver todos" vuelve a vaciar).
		onAnalysisCategoryChange({ id, values }) {
			const isNewCategory = id !== this.analysisCategory;
			this.fetchProfitabilityAnalysis({
				month: this.analysisMonth,
				category: id,
				selectedItems: isNewCategory
					? this.emptySelectionOf(id)
					: { [id]: [...(values || [])] },
			});
		},

		// --- Comparación año-contra-año de la card ---
		onToggleComparison(open) {
			this.comparisonOpen = open;
			if (!open) return;
			// YTD/Anual no aplican a la comparación: caemos a Mensual para que
			// el gráfico y los chips del header arranquen en una granularidad
			// válida (mismo criterio que Ventas).
			if (this.filterPeriod !== "month" && this.filterPeriod !== "quarter") {
				this.SET_FILTER_PERIOD("month");
			}
			// Una métrica que el tipo de negocio no tiene (Producto en
			// Servicios) vuelve a Ventas antes de pedir.
			if (!this.comparisonMetrics.some((m) => m.key === this.comparisonMetric)) {
				this.SET_COMPARISON_METRIC(COMPARISON_METRICS[0].key);
			}
			this.fetchProfitabilityComparison();
		},
		onComparisonSelectMetric(label) {
			const metric = this.comparisonMetrics.find((m) => m.label === label);
			if (!metric || metric.key === this.comparisonMetric) return;
			this.SET_COMPARISON_METRIC(metric.key);
			this.fetchProfitabilityComparison();
		},
		onComparisonAddYear(year) {
			this.SET_COMPARISON_YEARS([...this.comparisonYears, year]);
			this.fetchProfitabilityComparison();
		},
		onComparisonRemoveYear(year) {
			this.SET_COMPARISON_YEARS(this.comparisonYears.filter((y) => y !== year));
			this.fetchProfitabilityComparison();
		},
	},

	watch: {
		fetchSignature() {
			this.fetchReportData();
		},
		// El mes del header define el año en curso y su recorte: con la
		// comparación abierta se vuelve a pedir.
		endMonth() {
			if (this.comparisonOpen) this.fetchProfitabilityComparison();
		},
	},
};
</script>

<style scoped lang="scss">
.rentabilidad_tab {
	position: relative;
	padding-bottom: 40px;
	height: 100%;
}

// Header común vive en TabHeader.vue.

// Las dos cards de la fila de gráficos (patrón `.charts_row` de
// SalesChartsRow): mitad y mitad, una sola columna en pantallas angostas y
// con la comparación abierta.
.charts_row {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 16px;
	width: 100%;

	&.is-comparison {
		grid-template-columns: minmax(0, 1fr);
	}
}

@media (max-width: 1100px) {
	.charts_row {
		grid-template-columns: minmax(0, 1fr);
	}
}

.title_icon {
	width: 18px;
	height: 18px;
	color: var(--report-text-muted);
}

.tittle_section {
	font-size: 18px;
	font-weight: 600;
	color: var(--report-text);
}
</style>
